import { log } from "@app/app/services/log";

import { HostApiMethodData, HostApiMethodDefinitions, HostApiMethodName } from "../../api/methods";
import { ApiAdapter } from "../types";

const TEMP_API_NAMESPACE = "hc-ibe-temp-api";

/**
 * Window-based API adapter that handles communication through the window object
 * This adapter is used when the plugin is embedded in a host website
 */
export class WindowApiAdapter implements ApiAdapter {
    private WINDOW_API_NAMESPACE: string[];
    private registeredMethodHandlers: Partial<Record<string, (...args: unknown[]) => Promise<unknown>>> = {};
    private registeredEventListeners: Set<{
        eventName: string;
        callback: (data?: unknown) => unknown;
    }> = new Set();

    public constructor(namespace: string | string[]) {
        console.log("WindowApiAdapter constructor", { namespace });
        this.WINDOW_API_NAMESPACE = Array.isArray(namespace) ? namespace : [namespace];
        this.createWindowApiNamespace();
    }

    public setValue(key: string, value: any): void {
        this.getWindowApiNamespace()[key] = value;
    }

    public exposeApi(api: HostApiMethodData[]): void {
        log.debug("[WindowApiAdapter] Exposing API methods", [...api.map((m) => m.methodName)]);
        const windowApiNamespace = this.getWindowApiNamespace();

        // bind callback to windowApiNamespace
        api.forEach((method) => {
            windowApiNamespace[method.methodName] = (...args: unknown[]) => this.handleMethodCall(method.methodName, ...args);
        });

        // Bind event listener methods to windowApiNamespace
        windowApiNamespace["on"] = (eventName: string, callback: (data: unknown) => unknown) => {
            log.debug("[WindowApiAdapter] Registering event listener", { eventName });
            this.registeredEventListeners.add({ eventName, callback });
        };
        windowApiNamespace["off"] = (eventName: string, callback: (data: unknown) => unknown) => {
            log.debug("[WindowApiAdapter] Removing event listener", { eventName });
            this.registeredEventListeners.delete({ eventName, callback });
        };
    }

    public sendEventToHost(event: string, data?: unknown): void {
        const handlers = this.registeredEventListeners;

        handlers.forEach((handler) => {
            if (handler.eventName === event) {
                handler.callback(data);
            }
        });
    }

    public registerMethodHandler(method: string, callback: (...args: unknown[]) => Promise<unknown>): void {
        this.registeredMethodHandlers[method] = callback;

        this.checkAndFirePreRegisteredMethods(method);
    }

    private handleMethodCall(methodName: string, ...args: unknown[]): Promise<unknown> {
        const specificHandler = this.registeredMethodHandlers[methodName];

        if (specificHandler) {
            return specificHandler(...args);
        }

        log.debug("[WindowApiAdapter] No handler found for method", { methodName });

        return Promise.reject(new Error(`No handler registered for method: ${methodName}`));
    }

    private checkAndFirePreRegisteredMethods(method: string): void {
        const pendingCommands = this.getWindowApiNamespace()[TEMP_API_NAMESPACE] as
            | Array<{
                  name: string;
                  args: unknown[];
                  callback: (response: unknown) => void;
              }>
            | undefined;

        if (!pendingCommands || pendingCommands.length === 0) {
            return;
        }

        for (const command of pendingCommands) {
            if (command.name === method) {
                log.debug("[WindowApiAdapter] Executing pre-registered command", { method, command });
                this.handleMethodCall(command.name, ...command.args).then(command.callback);
                pendingCommands.splice(pendingCommands.indexOf(command), 1);
            }
        }
    }

    private getWindowApiNamespace(): Record<string, unknown> {
        const namespaceReference = getNestedObject(window, this.WINDOW_API_NAMESPACE);
        if (!namespaceReference) {
            throw new Error(`Namespace ${this.WINDOW_API_NAMESPACE} not found`);
        }
        return namespaceReference as Record<string, unknown>;
    }

    private createWindowApiNamespace(): void {
        ensureNestedObjects(window, this.WINDOW_API_NAMESPACE, {});
    }
}

function ensureNestedObjects(root: any, keys: string[], value: any = {}): any {
    return keys.reduce((obj, key) => (obj[key] = obj[key] || value), root);
}

function getNestedObject(root: any, keys: string[]): any | undefined {
    return keys.reduce((obj, key) => obj?.[key], root);
}
